import type { ReactElement } from 'react';
import type { ViewProps } from '../types';
import type { GetStaticProps, GetStaticPropsContext } from 'next';

import * as React from 'react';

// import { ContactA as View } from '../containers/ContactA';
import { View } from '../generated/contact';
import { BaseLayout } from '../layouts/BaseLayout';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { cleanSlug, getLocalizedPaths, getLocalizedSlugs } from '../utils/localize-helpers';
import { translateUrl } from 'next-translate-routes';
import { getNonPageNamespaces } from '../utils/get-non-page-namespaces';

export default function ContactPage(props: ViewProps) {
  const { pageContext } = props;
  const { t, i18n } = useTranslation(['common', 'contact']);
  const router = useRouter();
  const paths = getLocalizedPaths(router);
  const slugs = getLocalizedSlugs(router);
  const pageContextWithPaths = { ...pageContext, paths, slugs, slug: cleanSlug(router.pathname) };

  return (
    <>
      <NextSeo
        title={i18n.exists('contact:title', { fallbackLng: pageContextWithPaths.locale }) ? t('contact:title') : t('common:title')}
        description={i18n.exists('contact:description', { fallbackLng: pageContextWithPaths.locale }) ? t('contact:description') : t('common:description')}
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View
        {...props}
        title={i18n.exists('contact:headline', { fallbackLng: pageContextWithPaths.locale }) ? t('contact:headline') : t('common:headline')}
        body={i18n.exists('contact:body', { fallbackLng: pageContextWithPaths.locale }) ? t('contact:body') : t('common:body')}
        pageContext={pageContextWithPaths}
      />
    </>
  )
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
ContactPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType="contact" pageContext={pageContext}>{page}</BaseLayout>
  )
};

export const getStaticProps: GetStaticProps = (async (context: GetStaticPropsContext) => {
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const locale: string = (context.locale || context.defaultLocale) as string;
  const page = {
    title: null,
    body: null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    slugs: [],
    slug: 'contact',
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/contact', locale)}`,
        path: `${translateUrl('/contact', locale)}`,
        name: 'Contact', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
    ],
  };

  return {
    props: {
      title,
      body,
      global: {
        popular: [],
        latest: [],
      },
      pageContext,
      // Get our translations by namespace and locale.
      ...(await serverSideTranslations(locale as string, ['common', 'contact', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
});